import { motion } from "motion/react";
import { ReactNode } from "react";

/**
 * Card stack for the "How it works" section.
 */
export function HowItWorksSection() {
  return (
    <section className="relative flex h-full w-full flex-col justify-center bg-white py-20 lg:py-32">
      <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-8">
        
        {/* Header Row - Centered above columns */}
        <div className="mb-24 grid w-full grid-cols-1 gap-12 pb-8 text-center text-4xl font-semibold tracking-tight text-black md:grid-cols-3 md:gap-8 lg:text-6xl">
          <motion.div 
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6 }}
             className="flex justify-center"
          >
            How
          </motion.div>
          <motion.div 
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6, delay: 0.1 }}
             className="flex justify-center"
          >
            CoPlane
          </motion.div>
          <motion.div 
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             transition={{ duration: 0.6, delay: 0.2 }}
             className="flex justify-center"
          >
            Works
          </motion.div>
        </div>

        {/* Feature Columns */}
        <div className="grid gap-12 md:grid-cols-3 md:gap-8">
          <FeatureCard
            title="Govern"
            description="Establish secure connections between people, data, and systems; so teams start with confidence and compliance built in."
            icon={<div className="h-6 w-6 rounded-full bg-black" />}
            delay={0.3}
          />
          
          <FeatureCard
            title="Build"
            description="Design and test automated workflows in minutes, combining agents, rules, and human steps without breaking governance."
            icon={<div className="h-0 w-0 border-l-[10px] border-r-[10px] border-b-[16px] border-l-transparent border-r-transparent border-b-black" />}
            delay={0.4}
          />
          
          <FeatureCard
            title="Operate"
            description="Run, monitor, and adjust processes in real time, with full visibility into every action and result."
            icon={<div className="h-5 w-5 bg-black" />} // Simple square to match screenshot/previous
            delay={0.5}
          />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  title,
  description,
  icon,
  delay,
}: {
  title: string;
  description: string;
  icon: ReactNode;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, delay }}
      className="flex flex-col"
    >
      {/* Top Rail with Perspective */}
      <PerspectiveRail inverted />

      <div className="pt-8 pb-8">
        <div className="mb-6 flex h-12 w-12 items-center justify-center text-black">
            {icon}
        </div>
        <h3 className="mb-4 text-2xl font-bold text-black">{title}</h3>
        <p className="mb-8 text-lg leading-relaxed text-black/80">{description}</p>
      </div>

      {/* Bottom Rail with Perspective */}
      <div className="mt-auto">
        <PerspectiveRail />
      </div>
    </motion.div>
  );
}

function PerspectiveRail({ inverted = false }: { inverted?: boolean }) {
  return (
    <div className="relative h-[20px] w-full" style={{ perspective: "200px" }}>
      <div
        className="absolute inset-x-0 top-1/2 h-[12px] -translate-y-1/2 bg-black"
        style={{
          transform: `rotateX(${inverted ? "-45deg" : "45deg"})`,
          borderRadius: "1px",
        }}
      >
          {/* Optional: Add logo mark if desired, similar to Enterprise section 
              User said "mimic the enterprise page layout", which has logo marks on rails.
              But in the screenshot provided for "How It Works", the rails are plain black bars.
              I will stick to the screenshot (plain rails) unless otherwise requested.
          */}
      </div>
    </div>
  );
}

